// components/TranslationsProvider.js
'use client';

import { I18nextProvider } from 'react-i18next';
import { createInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { useMemo } from 'react';
import { TODOS } from '@/lib/idiomas';

const SUPPORTED_LOCALES = TODOS;
const DEFAULT_LOCALE = 'es';
const DEFAULT_NS = 'common';

/**
 * TranslationsProvider que recibe las traducciones PRE-CARGADAS del servidor.
 * Esto evita el flash de keys sin traducir (CLS).
 *
 * IMPORTANTE: Las traducciones se cargan en el servidor (layout.js) y se pasan aquí.
 * El cliente recibe el HTML ya traducido, sin necesidad de fetch adicional.
 *
 * @param {Object} props
 * @param {string} props.locale - El locale actual (es, en, pt, fr)
 * @param {Object} props.resources - Traducciones con formato { books: {...}, common: {...} }
 * @param {React.ReactNode} props.children
 */
export default function TranslationsProvider({ locale, resources, children }) {
  const safeLocale = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;

  const i18nInstance = useMemo(() => {
    const instance = createInstance();

    instance
      .use(initReactI18next)
      .init({
        // ✅ CLAVE: init SINCRÓNICO cuando ya tenés resources
        // (i18next 26 sacó initImmediate y lo ignora en silencio: ahora es initAsync)
        initAsync: false,

        // Idioma actual
        lng: safeLocale,
        fallbackLng: DEFAULT_LOCALE,
        supportedLngs: SUPPORTED_LOCALES,

        // Namespaces
        ns: Object.keys(resources || { [DEFAULT_NS]: {} }),
        defaultNS: DEFAULT_NS,

        // 🔑 Las traducciones vienen pre-cargadas del servidor
        resources: {
          [safeLocale]: resources || {},
        },

        interpolation: {
          escapeValue: false,
        },

        partialBundledLanguages: true,

        react: {
          useSuspense: false,
        },

        saveMissing: false,
        missingKeyHandler: false,
      });

    return instance;
  }, [safeLocale, resources]);

  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
}
